import { Injectable } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { environment } from '../../environments/environment';
import { ErrorService } from '../errorbar/error.service';
import { SipService } from '../sip/sip.service';

@Injectable()
export class WsService {

    private initWebrtcWatchdog: any;
    private initWebrtcTimeout = 10000;

    private initWsapiWatchdog: any;
    private initWsapiTimeout = 10000;

    private callAbortWatchdog: any;
    private callAbortTimeout = 3000;

    public wsOnline: boolean;

    private sipSubscription: Subscription;

    private connectObserver = new Subject<any>();
    public onConnect = this.connectObserver.asObservable();

    private errorObserver = new Subject<any>();
    public onError = this.errorObserver.asObservable();

    private stateObserver = new Subject<any>();
    public onState = this.stateObserver.asObservable();

    private userObserver = new Subject<any>();
    public onUser = this.userObserver.asObservable();

    private clientObserver = new Subject<any>();
    public onClient = this.clientObserver.asObservable();

    private workModeObserver = new Subject<any>();
    public onWorkMode = this.workModeObserver.asObservable();

    public ST = {
        UNAUTHORIZED: 0,
        INACTIVE: 1,
        INITWEBRTC: 2,
        PAUSED: 3,
        READY: 4,
        RINGING: 5,
        CALL: 6,
        POSTPROCESS: 7,
        ZOMBIE: 8,
        OUTBOUND: 9,
        SMSCHECK: 10,
        GUEST: 11,
        INVITEACCEPTED: 90,
        ECHOTEST: 91,
        TERMINATING: 92
    };

    public WM = {
        NORMAL: 'normal',
        TEST: 'test',
        TESTREQUIRED: 'testrequired'
    };

    public events: any;
    private observers: any;
    private wsMethods = [
        'login',
        'logout',
        'check_sms',
        'state',
        'pause',
        'incoming_call',
        'list_links',
        'list_results',
        'list_questions',
        'doc_list',
        'mech_list',
        'save_call_result',
        'redirect_call',
        'schedule_list',
        'mech_sms_list',
        'sendsms',
        'doc_accept',
        'test_call',
        'test_mode',
        'test_passed',
        'test',
        'debug_state',
        'script_list',
    ];

    private user: any;
    private state: any;
    private sipState: any;
    private credentials: any;
    private client: any;
    private workmode: any;
    private ws: any;

    private autoLeaveTestMode: boolean;

    constructor(private sip: SipService, private err: ErrorService) {
        this.initState();
        this.initObservers();
        this.startSocket();
        this.initWebrtcWatchdog = null;
        this.initWsapiWatchdog = null;
        this.autoLeaveTestMode = false;
        this.sipSubscription = this.sip.onSipState.subscribe(this.onSipState.bind(this));
    };

    public getUser(): any {
        return this.user;
    };

    public getState(): number {
        return this.state;
    };

    public getClient(): any {
        return this.client;
    };

    public getWorkMode(): any {
        return this.workmode;
    };

    /* API Methods Start */

    public methodTestCall(): void {
        this.callMethod('test_call');
    }

    public methodLogin(credentials: any): void {
        if (credentials.id_session && [this.ST.POSTPROCESS, this.ST.CALL, this.ST.TERMINATING].indexOf(this.state) > -1) {
            credentials.save_call_result = true;
        }
        this.credentials = credentials;
        this.callMethod('login', credentials);
    }

    public methodCheckSms(credentials: any): void {
        this.callMethod('check_sms', credentials);
    }

    public methodLogout(): void {
        this.callMethod('logout');
    }

    public methodRedirectCall(params: any): void {
        this.callMethod('redirect_call', params);
    }

    public methodPause(pauseState: any): void {
        this.sip.setPickupPolicy(!pauseState ? this.sip.PP.AUTOACCEPT : this.sip.PP.AUTOREJECT);
        this.callMethod('pause', { pause: pauseState });
    }

    public methodListLinks(): void {
        this.callMethod('list_links');
    }

    public methodListQuestions(): void {
        this.callMethod('list_questions');
    }

    public methodListResults(): void {
        this.callMethod('list_results');
    }

    public methodListActions(): void {
        this.callMethod('list_actions');
    }

    public methodListSchedules(params: any): void {
        this.callMethod('schedule_list', params);
    }

    public methodListDocs(): void {
        this.callMethod('doc_list', { page: 1 });
    }

    public methodDocAccept(docId: number): void {
        this.callMethod('doc_accept', { id_file: docId });
    }

    public methodSmsMechanic(mechanicId: number): void {
        this.callMethod('sendsms', { id_mech_sms: mechanicId });
    }

    public methodWorkMode(): void {
        this.callMethod('test_mode');
    }

    public methodGetScript(scriptId: number): void {
        this.callMethod('script', { id: scriptId });
    }

    public methodSaveCallResult(result: any): void {
        this.sip.setPickupPolicy(result.to_state === 3 ? this.sip.PP.AUTOREJECT : this.sip.PP.AUTOACCEPT);
        this.callMethod('save_call_result', result);
    }

    public methodDebugState(params: any): void {
        this.callMethod('debug_state', params);
    }

    /* API Methods End  */

    private initState(): void {
        this.credentials = {};
        this.setUser(null);
        this.setClient({});
        this.setState(0);
        this.sipState = null;
        this.workmode = this.WM.NORMAL;
    };

    public setState(state: number): void {
        if (this.state === state) {
            return;
        }
        if (this.state === this.ST.CALL && state === this.ST.POSTPROCESS && this.sipState && this.sipState.status === 'call_accepted' && this.credentials.save_call_result) {
            // игнорируем POSTPROCESS после возобновления вебсокета, если еще разговариваем
            return;
        }
        if ([this.ST.RINGING, this.ST.CALL, this.ST.POSTPROCESS].indexOf(this.state) > -1 && state === this.ST.INVITEACCEPTED) {
            // игнорируем INVITEACCEPTED если ранее уже пришел статус 5, 6 или 7
            return;
        }
        if ([this.ST.INVITEACCEPTED, this.ST.RINGING, this.ST.CALL].indexOf(this.state) === -1 && state === this.ST.TERMINATING) {
            // игнорируем TERMINATING если только не находимся в статусе 5 или 6
            return;
        }
        if ([this.ST.RINGING, this.ST.CALL, this.ST.POSTPROCESS].indexOf(this.state) > -1 && state === this.ST.INITWEBRTC && this.credentials.save_call_result) {
            // игнорируем INITWEBRC если еще не сохранили результат
            return;
        }
        clearTimeout(this.callAbortWatchdog);
        this.credentials.save_call_result = false;
        if (state === this.ST.INITWEBRTC && !this.sip.state.online) {
            if (this.user && this.user.sip && this.user.sip_pwd) {
                const sipLogin = this.user.sip.replace('SIP/', '');
                this.sip.setSipUser({ login: sipLogin, password: this.user.sip_pwd });
                if (this.initWebrtcWatchdog) {
                    clearTimeout(this.initWebrtcWatchdog);
                } else {
                    this.err.logError('debug', 'WsService Starting SIP Session');
                    this.sip.initWebRtc();
                }
                this.initWebrtcWatchdog = setTimeout(this.restartWebRtc.bind(this), this.initWebrtcTimeout);
            }
        }
        this.err.logError('debug', 'WsService StateChange: ' + state);
        this.state = state;
        this.stateObserver.next(state);
        if (state === this.ST.UNAUTHORIZED) {
            this.sip.deinitWebRtc();
        } else if ([this.ST.PAUSED, this.ST.READY].indexOf(this.state) > -1) {
            if (this.initWebrtcWatchdog) {
                clearTimeout(this.initWebrtcWatchdog);
            }
        }
    }

    private setUser(user: any): void {
        this.user = user;
        this.userObserver.next(user);
    }

    private setClient(client: any): void {
        this.client = client;
        this.clientObserver.next(client);
    }

    private setWorkMode(mode: any): void {
        this.workmode = mode;
        this.workModeObserver.next(mode);
        this.sip.setTestMode(mode !== this.WM.NORMAL);
        this.err.logError('debug', 'WsService WorkModeChange: ' + mode);
    }

    private checkWorkMode(data: any): void {
        if (data.hasOwnProperty('is_test_req')) {
            if (data.is_test_req) {
                this.setWorkMode(this.WM.TESTREQUIRED);
            } else if (this.workmode === this.WM.TESTREQUIRED) {
                this.autoLeaveTestMode = true;
                this.setWorkMode(this.WM.TEST);
            }
        } else {
            if (data.hasOwnProperty('mode')) {
                this.setWorkMode(data.mode);
            }
        }
    }

    private restartWebRtc(): void {
        if (!this.sip.state.online) {
            this.err.logError('debug', 'WsService Restarting SIP Session');
            this.sip.deinitWebRtc();
            this.sip.initWebRtc();
            this.initWebrtcWatchdog = setTimeout(this.restartWebRtc.bind(this), this.initWebrtcTimeout);
        }
    }

    private initObservers(): void {
        this.observers = [];
        this.events = [];
        const len = this.wsMethods.length;
        for (var j = 0; j < len; j++) {
            const methodName = this.wsMethods[j];
            this.observers[methodName] = new Subject<any>();
            this.events[methodName] = this.observers[methodName].asObservable();
        }
    };

    private updateState(data: any): void {
        if (data.method === 'login') {
            if (data.error === 0) {
                if (data.hasOwnProperty('state')) {
                    data.login = this.credentials.login;
                    this.setUser(data);
                    this.setState(data.state);
                    this.methodListLinks();
                    this.checkWorkMode(data);
                }
            } else {
                this.credentials = {};
                this.setUser(null);
            }
        }
        if (data.method === 'logout') {
            this.setUser(null);
            this.initState();
        }
        if (data.method === 'state') {
            this.err.logError('debug', 'WsService received state: ' + data.state);
            this.setState(data.state);
            this.checkWorkMode(data);
        }
        if (data.method === 'test') {
            this.checkWorkMode(data);
        }
        if (data.method === 'incoming_call' || data.hasOwnProperty('id_call_reason')) {
            this.setClient(data);
        }
    };

    private callMethod(methodName: string, methodData?: any): void {
        if (!this.ws || !this.wsOnline) {
            return;
        }
        const data: any = { method: methodName };
        if (methodData) {
            data.params = methodData;
        }
        this.err.logError('debug', 'WsService: Sending packet ' + JSON.stringify(data));
        try {
            this.ws.send(JSON.stringify(data));
        } catch (e) {
            this.err.logError('debug', 'WsService: Error sending packet');
            console.log(e);
        }
    }

    private startSocket(): void {
        this.wsOnline = false;
        this.ws = new WebSocket(environment.WS_API.PROTOCOL + '://' + environment.WS_API.HOST + environment.WS_API.PATH);
        this.ws.onmessage = this.onWsMessage.bind(this);
        this.ws.onopen = this.onWsConnect.bind(this);
        this.ws.onclose = this.onWsDisconnect.bind(this);
        this.err.logError('debug', 'WsService: Starting WS connection');
    }

    private restartSocket(): void {
        if (this.initWsapiWatchdog) {
            clearTimeout(this.initWsapiWatchdog);
        }
        if (this.ws) {
            this.ws.close();
            delete this.ws;
            this.ws = null;
            this.err.logError('debug', 'WsService: Closing WS connection');
        }
        this.startSocket();
        this.initWsapiWatchdog = setTimeout(this.restartSocket.bind(this), this.initWsapiTimeout);

    }

    private onWsConnect(event): void {
        if (this.initWsapiWatchdog) {
            clearTimeout(this.initWsapiWatchdog);
        }
        this.wsOnline = true;
        this.connectObserver.next();
        this.err.logError('debug', 'WsService: WS connected');
    }

    private onWsDisconnect(event): void {
        this.wsOnline = false;
        this.connectObserver.next();
        this.err.logError('error', 'WS Connection lost, reconnecting...');
        this.initWsapiWatchdog = setTimeout(this.restartSocket.bind(this), this.initWsapiTimeout);
        this.err.logError('debug', 'WsService: WS disconnected');
    }

    private onWsMessage(event): void {
        let data: any = {};
        this.err.logError('debug', 'WsService: Received packet ' + event.data);
        try {
            data = JSON.parse(event.data);
        } catch (e) {
            this.err.logError('error', 'WsAPI Data Error');
            return;
        }
        if (!data.method) {
            this.err.logError('error', 'WsAPI Invalid Answer');
            return;
        }
        if (!this.observers.hasOwnProperty(data.method)) {
            this.err.logError('error', 'WsAPI Unknown Answer <' + data.method + '>');
            return;
        }
        this.updateState(data);
        this.observers[data.method].next(data);
        if (data.error) {
            this.err.logError('error', 'Error (' + data.error + ') on method <' + data.method + '>');
        }
    }

    private onSipCallAbort(): void {
        this.setState(this.ST.PAUSED);
    }

    private onSipState(state: any): void {
        this.err.logError('debug', 'WsService: Running with version ' + environment.CODEVERSION);
        if (this.sipState && this.sipState.status) {
            this.err.logError('debug', 'SipService State is: ' + this.sipState.status);
        }
        if (state && state.status) {
            this.err.logError('debug', 'SipService State changes to: ' + state.status);
        }

        if (state && state.status === 'call_accepted' && this.sipState && this.sipState.status === 'ready') {
            this.setState(this.ST.INVITEACCEPTED);
        }
        if (state && state.status === 'echo_test') {
            this.setState(this.ST.ECHOTEST);
        }
        if (state && state.status === 'ready' && this.sipState && this.sipState.status === 'echo_test') {
            this.setState(this.ST.PAUSED);
            if (this.autoLeaveTestMode) {
                this.autoLeaveTestMode = false;
                this.methodWorkMode();
            }
        }
        let callEnded = false;
        let callAborted = false;
        if (state && state.status === 'ready' && this.sipState && this.sipState.status === 'call_accepted') {
            if (this.state === this.ST.CALL) {
                callEnded = true;
            } else if (this.state === this.ST.RINGING) {
                callAborted = true;
            }
        }
        if (state && state.status === 'call_aborted' && this.sipState && this.sipState.status !== 'call_aborted') {
            callAborted = true;
        }
        this.sipState = {
            online: state.online,
            status: state.status,
            permission: state.permission
        };
        if (callEnded) {
            this.setState(this.ST.TERMINATING);
        }
        if (callAborted) {
            clearTimeout(this.callAbortWatchdog);
            this.callAbortWatchdog = setTimeout(this.onSipCallAbort.bind(this), this.callAbortTimeout);
            this.err.logError('warn', 'Вызов прерван клиентом');
        }
        if (state && state.status === 'idle_call') {
            this.onSipCallAbort();
        }
    }

}



// WEBPACK FOOTER //
// ./src/app/ws/ws.service.ts